import { differenceInMinutes, OwaDate, getYear, getMonth, getDate, addMinutes } from 'owa-datetime';
import FindMeetingTimesResponse from 'owa-calendar-scheduling-service/lib/schema/FindMeetingTimesResponse';
import getFindMeetingTimeSuggestionsRequest, {
    getFindMeetingTimesSuggestionsRequestForReschedule,
} from '../utils/getFindMeetingTimeSuggestionsRequest';
import { findMeetingTimesService } from 'owa-calendar-scheduling-service/lib/services/findMeetingTimesService';
import { getStore } from '../store/store';
import { hasAttendees } from 'owa-calendar-event-capabilities';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { orchestrator } from 'satcheljs';
import { refreshTimeSuggestions } from '../actions/publicActions';
import {
    setPreviousDate,
    setPreviousDurationInMinutes,
    updateTimeSuggestions,
    setTimeSuggestionsRequestState,
    setLastFindMeetingTimesRequestId,
    setLastFindMeetingTimesRequestDate,
} from '../actions/internalActions';
import { TimeSuggestionsRequestState } from '../store/schema/TimeSuggestionsRequestState';
import {
    clearTimeSuggestionStartFirstSet,
    logTimeSuggestionStartFirstSet,
    logFindMeetingTimesRequestId,
} from 'owa-calendar-compose-form-actions';
import { getTimeRangeFromTimingSearchCriteria } from '../utils/getTimeRangeFromTimingSearchCriteria';
import logTimeSuggestionsRequestSummary from '../utils/logTimeSuggestionsMetrics';
import {
    shouldShowNoSuggestionsFound,
    showNoSuggestionsFound,
    resetTimeSuggestionData,
} from '../utils/timeSuggestionsRequestUtils';
import TimingType from '../store/schema/TimeSuggestionsTimingTypes';
import { isDateAfterNextWeek } from '../utils/isDateAfterNextWeek';
import { updateSearchCriteriaTiming } from '../mutators/searchCriteriaDialogMutators';
import { isAnyTimeSuggestionsExpEnabled } from '../utils/isAnyExperimentEnabled';
import { getDefaultDurationForShortenedDuration } from '../utils/durationOptionsUtils';

orchestrator(refreshTimeSuggestions, async actionMessage => {
    const { previousStart, previousMeetingDurationInMinutes } = getStore();
    const { event, attendeesChanged } = actionMessage;

    if (!hasAttendees(event, true)) {
        // No attendees, no suggestions
        doNotShowSuggestions();
        return;
    }

    if (shouldShowNoSuggestionsFound(event)) {
        // Show "no time suggestions found" if it's all day/recurring event or if the event has invalid attendees
        showNoSuggestionsFound();
        return;
    }

    const owaStartDate = event.Start;
    let owaEndDate = event.End;
    const currentDuration = differenceInMinutes(owaEndDate, owaStartDate);

    if (currentDuration <= 0) {
        // Show "no time suggestions found" if the duration of the event is 0
        showNoSuggestionsFound();
        return;
    }

    // If owaStartDate is further than next week set timingSearchCriteria to next available
    if (isDateAfterNextWeek(owaStartDate)) {
        updateSearchCriteriaTiming(TimingType.NextAvailable);
    }

    if (
        attendeesChanged /* if attendees changed */ ||
        didDateOrTimeZoneChange(previousStart, owaStartDate) ||
        previousMeetingDurationInMinutes != currentDuration /* if duration changed */
    ) {
        let request;

        // Adjust the event duration to not be affected by shortenedEvent settings for the findmeetingtimesrequest
        // so the start late - end early time ranges are Free for the user.
        // Ex. And adjusted duration of 22 mins (shortenedEvent setting = StartLate by 8 mins)
        // will be requested as a timeslot of 30 mins, and will be shortened later (in convertMeetingSlotIntoOwaDates)
        // for display purposes
        owaEndDate = addMinutes(
            owaStartDate,
            getDefaultDurationForShortenedDuration(currentDuration)
        );

        if (isAnyTimeSuggestionsExpEnabled()) {
            const { start: rangeStart, end: rangeEnd } = getTimeRangeFromTimingSearchCriteria(
                owaStartDate,
                getStore().timingSearchCriteria
            );

            const durationToRequest = differenceInMinutes(owaEndDate, owaStartDate);

            request = getFindMeetingTimesSuggestionsRequestForReschedule(
                event,
                rangeStart,
                rangeEnd,
                durationToRequest
            );
        } else {
            request = getFindMeetingTimeSuggestionsRequest(event, owaStartDate, owaEndDate);
        }
        setTimeSuggestionsRequestState(TimeSuggestionsRequestState.SearchingSuggestions);
        let response = await findMeetingTimesService(request, 'suggestions');
        if (response.headers?.has('request-id')) {
            logFindMeetingTimesRequestId(response.headers.get('request-id'));
        }

        // Capture the date time of the request as close to the time it came back from the server in the absence of the "date" header in the request's response
        // Date.now() already captures the numberof milliseconds since 1970 in UTC
        let lastFindMeetingTimesRequestDate = new Date(Date.now());

        // Validate if the meeting changed while waiting for the server response
        if (shouldShowNoSuggestionsFound(event)) {
            showNoSuggestionsFound();
            return;
        }

        let timeSuggestions: FindMeetingTimesResponse;
        if (isSuccessStatusCode(response.status)) {
            timeSuggestions = await response.json();
            if (timeSuggestions.MeetingTimeSuggestions?.[0]) {
                updateTimeSuggestions(timeSuggestions);
                setLastFindMeetingTimesRequestId(response.headers.get('request-id'));
                setLastFindMeetingTimesRequestDate(lastFindMeetingTimesRequestDate);
                setTimeSuggestionsRequestState(TimeSuggestionsRequestState.ShowSuggestions);
            } else {
                // If there are no suggestions returned from the server, then show "no time suggestions found"
                showNoSuggestionsFound();
            }
        } else {
            // If time suggestions failed, then show "no time suggestions found"
            showNoSuggestionsFound();
        }
        setPreviousDate(owaStartDate);
        setPreviousDurationInMinutes(currentDuration);

        if (attendeesChanged) {
            logTimeSuggestionStartFirstSet();
        }

        logTimeSuggestionsRequestSummary(
            event,
            response.status,
            timeSuggestions?.MeetingTimeSuggestions?.length,
            timeSuggestions?.EmptySuggestionsReason
        );
    }
});

/**
 * Function which hides time suggestion and clears time suggestions data
 */
function doNotShowSuggestions() {
    setTimeSuggestionsRequestState(null);
    resetTimeSuggestionData();
    clearTimeSuggestionStartFirstSet();
}

function didDateOrTimeZoneChange(previousDate: OwaDate, currentDate: OwaDate): boolean {
    // If timezone is changed or when the date is different, we make cas calls.
    // If just time is changed, then no cas call
    return (
        !previousDate ||
        !(
            previousDate.tz == currentDate.tz &&
            getDate(previousDate) == getDate(currentDate) &&
            getMonth(previousDate) == getMonth(currentDate) &&
            getYear(previousDate) == getYear(currentDate)
        )
    );
}
